import { buddhaCampaign } from "./buddha.js";
import { engineCampaign } from "./engine.js";
import { engineV2Campaign } from "./engine-v2.js";
import { sanctifiedCampaign } from "./sanctified.js";
import type { CampaignConfig } from "./types.js";

export const campaigns = {
  engine: engineCampaign,
  "engine-v2": engineV2Campaign,
  sanctified: sanctifiedCampaign,
  buddha: buddhaCampaign,
} as const satisfies Record<string, CampaignConfig>;

export type CampaignId = keyof typeof campaigns;

export interface CampaignLocationLike {
  pathname?: string;
  search?: string;
}

function isCampaignId(value: string | null): value is CampaignId {
  return value === "engine" || value === "engine-v2" || value === "sanctified" || value === "buddha";
}

function getSearchCampaign(search: string | undefined): CampaignId | "unknown" | null {
  const params = new URLSearchParams(search ?? "");
  if (!params.has("campaign")) return null;
  const queryCampaign = params.get("campaign")?.toLowerCase() ?? null;
  return isCampaignId(queryCampaign) ? queryCampaign : "unknown";
}

function getPathCampaign(pathname: string | undefined): CampaignId | null {
  const firstSegment = (pathname ?? "").split("/").filter(Boolean)[0]?.toLowerCase() ?? null;
  if (firstSegment === "buddha-defense") return "buddha";
  if (firstSegment === "engine-v2") return "engine-v2";
  return isCampaignId(firstSegment) ? firstSegment : null;
}

export function resolveCampaignId(locationLike?: CampaignLocationLike): CampaignId {
  const queryCampaign = getSearchCampaign(locationLike?.search);
  if (queryCampaign) return queryCampaign === "unknown" ? "sanctified" : queryCampaign;
  return getPathCampaign(locationLike?.pathname) ?? "sanctified";
}

export function shouldShowCampaignSelector(locationLike?: CampaignLocationLike): boolean {
  if (getSearchCampaign(locationLike?.search)) return false;
  return getPathCampaign(locationLike?.pathname) === null;
}

const currentLocation =
  typeof globalThis.location === "undefined"
    ? undefined
    : { pathname: globalThis.location.pathname, search: globalThis.location.search };

export const activeCampaign: CampaignConfig = campaigns[resolveCampaignId(currentLocation)];

export type {
  CampaignConfig,
  CampaignAuthoredField,
  CampaignAuthoredFieldSource,
  CampaignMapConfig,
  CampaignStoryConfig,
  CampaignStoryPanel,
  CampaignUiConfig,
  CampaignVisualConfig,
} from "./types.js";
